// ─────────────────────────────────────────────────────────────
//  Team-Provisionsrechner – reine Rechenlogik aus
//  provisions_rechner.html (Sales 40 % / Rest 60 %, Bonus-Staffel,
//  Ops-Split, Fixum-Sicherheitsnetz, optionaler Cold-Caller).
// ─────────────────────────────────────────────────────────────

export const OUTREACH_FIXUM = 200;
export const OWNER_FIXUM = 100;
export const APPT_MIN = 1;
export const APPT_MAX = 50;

export function parseShareSpec(raw) {
  if (raw == null) return null;
  let s = String(raw).trim();
  if (!s) return null;
  try { s = decodeURIComponent(s); } catch { /* roh verwenden */ }
  s = s.replace(/^\?/, '').trim();
  if (!s) return null;

  const named = new URLSearchParams(s);
  const namedPrice = parseInput(named.get('betrag') || named.get('preis') || named.get('p') || '');
  const namedAppt = parseInput(named.get('termine') || named.get('t') || named.get('n') || '');
  const namedFixum = parseInput(named.get('fixum') || named.get('f') || '');
  const namedIncluded = parseInput(named.get('inkl') || named.get('included') || named.get('i') || '');
  const withFixum = (out) => {
    const next = { ...out };
    if (namedFixum > 0 || out.fixum) next.fixum = out.fixum || String(namedFixum);
    const included = namedIncluded > 0 ? namedIncluded : parseInput(out.included);
    if (included > 0) next.included = String(Math.round(included));
    return next;
  };

  if (namedPrice > 0 || namedAppt > 0) {
    return withFixum({
      price: namedPrice > 0 ? String(namedPrice) : null,
      appointments: namedAppt > 0 ? String(Math.round(namedAppt)) : null,
    });
  }

  const compact = s.match(/^(\d+(?:[.,]\d+)?)(?:\s*[x×*&-]\s*(\d+))?(?:&(?:fixum|f)=(\d+(?:[.,]\d+)?))?(?:&(?:inkl|included|i)=(\d+))?$/i);
  if (compact) {
    const price = parseInput(compact[1]);
    const appointments = compact[2] ? parseInt(compact[2], 10) : 0;
    const compactFixum = parseInput(compact[3] || '');
    const compactIncluded = parseInput(compact[4] || '');
    if (price <= 0) return null;
    return withFixum({
      price: String(price),
      appointments: appointments > 0 ? String(appointments) : null,
      ...(compactFixum > 0 ? { fixum: String(compactFixum) } : {}),
      ...(compactIncluded > 0 ? { included: String(Math.round(compactIncluded)) } : {}),
    });
  }

  const parts = s.split('&').map((p) => p.trim()).filter(Boolean).filter((p) => !/^(fixum|f|inkl|included|i)=/i.test(p));
  if (parts.length >= 1 && /^\d/.test(parts[0])) {
    const price = parseInput(parts[0]);
    const appointments = parts[1] ? parseInt(parts[1], 10) : 0;
    if (price <= 0) return null;
    return withFixum({
      price: String(price),
      appointments: appointments > 0 ? String(appointments) : null,
    });
  }

  return null;
}

export function readShareFromLocation(pathname, search) {
  const fromQuery = parseShareSpec(search);
  if (fromQuery) return fromQuery;
  const m = String(pathname || '').match(/\/teampayout-([^/?#]+)/i)
    || String(pathname || '').match(/\/teamprovi-([^/?#]+)/i);
  return m ? parseShareSpec(m[1]) : null;
}

export function teamProviBasePath(pathname) {
  const path = String(pathname || '/teampayout');
  const de = path.startsWith('/de');
  return de ? '/de/teampayout' : '/teampayout';
}

export function buildShareQuery(price, appointments, fixum, included) {
  const p = Number(price);
  if (!(p > 0)) return '';
  const priceStr = Number.isInteger(p) ? String(p) : String(p);
  const a = Math.round(Number(appointments) || 0);
  const f = Number(fixum) || 0;
  const incl = Math.round(Number(included) || 0);
  const base = a > 0 ? `?${priceStr}x${a}` : `?${priceStr}`;
  if (!(f > 0) && !(incl > 0)) return base;
  let q = base;
  if (f > 0) q += `&fixum=${Number.isInteger(f) ? String(f) : String(f)}`;
  if (incl > 0) q += `&inkl=${incl}`;
  return q;
}

export function parseInput(str) {
  let s = String(str ?? '').trim().replace(/[^\d,.-]/g, '');
  if (s.includes(',') && s.includes('.')) {
    s = s.replace(/\./g, '').replace(',', '.');
  } else if (s.includes(',')) {
    s = s.replace(',', '.');
  }
  const n = parseFloat(s);
  return Number.isNaN(n) ? 0 : n;
}

export function bonusTiersForPrice(base) {
  if (base < 200) return [8, 15, 22, 35];
  if (base <= 400) return [5, 10, 15, 25];
  return [4, 8, 12, 20];
}

export function priceClassLabel(base) {
  if (base < 200) return '< 200 € / Termin';
  if (base <= 400) return '200–400 € / Termin';
  return '> 400 € / Termin';
}

function bonusForAppointments(appointments, tiers) {
  if (appointments >= tiers[3]) return { pct: 0.08, label: `8% Bonus aktiv · ab ${tiers[3]} Terminen` };
  if (appointments >= tiers[2]) return { pct: 0.06, label: `6% Bonus aktiv · ab ${tiers[2]} Terminen` };
  if (appointments >= tiers[1]) return { pct: 0.04, label: `4% Bonus aktiv · ab ${tiers[1]} Terminen` };
  if (appointments >= tiers[0]) return { pct: 0.02, label: `2% Bonus aktiv · ab ${tiers[0]} Terminen` };
  return { pct: 0, label: 'Kein Bonus aktiv' };
}

export function calcTeamProvi({ base, appointments, extraRole, fixum, included }) {
  const price = Number(base) || 0;
  const mult = Number(appointments) || 1;
  const fixumAmount = Math.max(Number(fixum) || 0, 0);
  const includedCount = Math.max(Math.round(Number(included) || 0), 0);
  const covered = Math.min(includedCount, mult);
  const billableAppointments = Math.max(mult - covered, 0);
  const total = price * billableAppointments + fixumAmount;

  const salesRep = total * 0.20;
  const salesInternal = total * 0.20;
  const sales = salesRep + salesInternal;
  const rest = total - sales;

  const tiers = bonusTiersForPrice(price);
  const bonus = bonusForAppointments(mult, tiers);

  const companyShare = 0.50 - bonus.pct;
  const opsShare = 0.50 + bonus.pct;
  const company = rest * companyShare;
  const ops = rest * opsShare;

  const outreachSplit = extraRole ? 0.45 : 0.65;
  const ownerSplit = extraRole ? 0.25 : 0.35;
  const extraSplitFromOps = extraRole ? 0.30 : 0;
  const extraTopUpFromCompany = extraRole ? ops * 0.05 : 0;

  const outreachCalc = ops * outreachSplit;
  const ownerCalc = ops * ownerSplit;
  const extraFromOps = ops * extraSplitFromOps;

  const outreach = Math.max(outreachCalc, OUTREACH_FIXUM);
  const owner = Math.max(ownerCalc, OWNER_FIXUM);
  const extra = extraFromOps + extraTopUpFromCompany;

  const opsNeeded = outreach + owner + extraFromOps;
  const opsDeficit = Math.max(opsNeeded - ops, 0);
  const companyFinal = company - opsDeficit - extraTopUpFromCompany;
  const opsFinal = outreach + owner + extra;

  return {
    base: price,
    appointments: mult,
    fixum: fixumAmount,
    included: includedCount,
    covered,
    billableAppointments,
    total,
    salesRep,
    salesInternal,
    rest,
    tiers,
    bonusPct: bonus.pct,
    bonusLabel: bonus.label,
    priceClassLabel: `Preisklasse: ${priceClassLabel(price)} · Stufen: ${tiers.join(' / ')}`,
    outreachSplit,
    ownerSplit,
    extraSplitFromOps,
    extraPctDisplay: extraRole ? Math.round(extraSplitFromOps * 100 + 5) : 0,
    outreachCalc,
    ownerCalc,
    outreach,
    owner,
    extra,
    extraFromOps,
    extraTopUpFromCompany,
    opsDeficit,
    companyFinal,
    opsFinal,
    outreachFixumActive: outreachCalc < OUTREACH_FIXUM,
    ownerFixumActive: ownerCalc < OWNER_FIXUM,
    companyNegative: companyFinal < 0,
    companyPctOfRest: rest > 0 ? Math.round((companyFinal / rest) * 100) : 0,
    opsPctOfRest: rest > 0 ? Math.round((opsFinal / rest) * 100) : 0,
    outreachPerTermin: mult > 0 ? outreach / mult : 0,
    ownerPerTermin: mult > 0 ? owner / mult : 0,
  };
}
